/* Plantica — Video produs: opening a clip

   The cards are pictures with a button on them until someone asks for a clip.
   Each card carries its player in a `<template>`, which is inert markup: no
   request, no frame, no sound. This copies that template into the modal on the
   click, and empties the modal when it closes — which is also how the clip is
   stopped, since there is no player left to keep playing. A YouTube frame in
   particular cannot be paused from outside; removing it is the only way.

   The modal is the theme's `basic-modal`, and `show()` is called directly:
   the theme binds its `aria-controls` listeners once, when the modal connects,
   so an attribute added later is ignored.

   The clip has the player's own controls (06.10.2026; it had none between 03
   and 06.10). It is started here, inside the click that opened it, rather than
   by its `autoplay` attribute alone: a clip with sound may only start on a
   person's gesture, and Safari does not count the attribute as one. If a
   browser refuses all the same, the controls are there to start it by hand.

   Without this file the cards are still pictures and the row still scrolls;
   nothing plays, and nothing pretends it will.

   Custom file. See CUSTOMIZATIONS.md. */

(() => {
  const RATIOS = {
    tall: '9 / 16',
    wide: '16 / 9',
  };

  const stageOf = (modal) => modal.querySelector('[data-video-stage]');

  const clear = (modal) => {
    const stage = stageOf(modal);
    if (!stage) return;

    // Pause first: a <video> that is removed mid-play keeps its audio running
    // for a moment in some browsers, and that moment is very loud.
    stage.querySelectorAll('video').forEach((video) => {
      try {
        video.pause();
      } catch (error) {
        /* nothing to do */
      }
    });

    stage.replaceChildren();
  };

  const start = (stage) => {
    const video = stage.querySelector('video');
    if (!video) return;
    video.play().catch(() => {
      /* refused: the controls are there */
    });
  };

  /* The modal is closed by the theme — by Escape, by the backdrop, by its own
     button — and none of those tell us. The attribute it drops is what we
     watch. */
  const watch = (modal) => {
    if (modal.dataset.videoWatched) return;
    modal.dataset.videoWatched = 'true';

    new MutationObserver(() => {
      if (!modal.hasAttribute('open') && !modal.hasAttribute('active')) clear(modal);
    }).observe(modal, { attributes: true, attributeFilter: ['open', 'active'] });
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-video-open]');
    if (!trigger || trigger.disabled) return;

    const modal = document.getElementById(trigger.dataset.videoOpen);
    const template = trigger.closest('.plantica-video__card').querySelector('[data-video-player]');
    if (!modal || !template) return;

    const stage = stageOf(modal);
    if (!stage) return;

    event.preventDefault();
    watch(modal);

    const shape = trigger.dataset.videoShape === 'tall' ? 'tall' : 'wide';
    modal.querySelector('.plantica-video-modal__inner').style.setProperty(
      '--plantica-video-ratio',
      RATIOS[shape]
    );

    stage.replaceChildren(template.content.cloneNode(true));

    if (typeof modal.show === 'function') modal.show();
    else modal.setAttribute('open', '');

    // After `show`, which moves the modal to <body>: the clip starts where it
    // will stay, and still inside this click.
    start(stage);
  });
})();
